import type { StoreOrder } from './api';

/**
 * What the payment result screen says, decided from the order the API returns.
 *
 * WHY THE ATTEMPT AND NOT THE ORDER: a failed online payment never moves the
 * order. It stays at `to_pay`, still payable, and only the gateway attempt says
 * `fail`. Branching on the order's status alone is what sent a failed card
 * payment to the upload-your-proof screen.
 */
export type OrderOutcome =
  /** Cancelled or refunded: nothing left to pay. */
  | 'cancelled'
  /** What it buys has arrived (see `orderDelivered`). */
  | 'success'
  /** The last online attempt failed or was voided; the same order can be paid again. */
  | 'gateway_failed'
  /** An online order nobody has paid, and the member is not just back from the gateway. */
  | 'gateway_unpaid'
  /** Waiting for the member to send the money and upload a proof. */
  | 'manual_pending'
  /** Confirming: back from the gateway, money on its way, or paid with the plan opening. */
  | 'waiting';

export type OutcomeOrder = Pick<
  StoreOrder,
  'status' | 'paymentMethodCode' | 'paymentMethodSettlement' | 'gatewayPayment' | 'unlocks'
>;

/**
 * What the order buys, from the API's `unlocks`: the coaching plan, learning
 * (courses or a creator's subscription), or goods. An older API sends no
 * `unlocks`; the coaching plan was all this screen sold then.
 */
export type OrderKind = 'coaching' | 'learning' | 'credits' | 'goods';

export function orderKind(order: Pick<StoreOrder, 'unlocks'>): OrderKind {
  const u = order.unlocks;
  if (!u || u.coachingPlan) return 'coaching';
  if (u.courseIds.length > 0 || u.subscriptionCreatorIds.length > 0) return 'learning';
  if ((u.credits ?? 0) > 0) return 'credits';
  return 'goods';
}

const PAYABLE = new Set(['pending', 'to_pay']);
const POLLED = new Set(['pending', 'to_pay', 'paid']);
const DEAD_ATTEMPT = new Set(['fail', 'void']);
/** Money is moving: `pending` is an over-the-counter reference that can still be paid. */
const LIVE_ATTEMPT = new Set(['pending', 'success']);

/**
 * An online order, by its method's settlement. A method removed from the
 * registry since checkout has no settlement, and then an attempt on record is
 * the evidence.
 */
export function isGatewayOrder(order: OutcomeOrder): boolean {
  if (order.paymentMethodSettlement) return order.paymentMethodSettlement === 'gateway';
  return order.gatewayPayment != null;
}

/**
 * Where "Start learning" leads once a learning order completes. One course:
 * straight into it. A SUBSCRIPTION enrolls the member in nothing until they
 * open a course, so My courses would greet them with "not enrolled in any
 * course"; they go to Browse instead, where the courses it opens read
 * "Included".
 */
export function learningLanding(unlocks: StoreOrder['unlocks']): string {
  if ((unlocks?.subscriptionCreatorIds.length ?? 0) > 0) return '/learn/browse';
  if (unlocks?.courseIds.length === 1) return `/learn/${unlocks.courseIds[0]}`;
  return '/learn';
}

/**
 * Has what this order buys actually arrived? The promise differs by kind: a
 * coaching plan is "you are on Pro" (paid is not enough until the plan reads
 * back), learning opens when the order COMPLETES (that is what grants the
 * course or the window), and goods are done once paid (shipping follows).
 */
export function orderDelivered(order: OutcomeOrder, ctx: { isPro: boolean }): boolean {
  const { status } = order;
  switch (orderKind(order)) {
    case 'coaching':
      return ctx.isPro && (status === 'paid' || status === 'completed');
    case 'learning':
      return status === 'completed';
    // A pack's credits are granted when the order COMPLETES (the virtual
    // auto-complete right after payment), the same moment a course opens.
    case 'credits':
      return status === 'completed';
    case 'goods':
      return !PAYABLE.has(status) && status !== 'cancelled' && status !== 'refunded';
  }
}

export function orderOutcome(
  order: OutcomeOrder,
  ctx: { returned: boolean; delivered: boolean },
): OrderOutcome {
  const { status } = order;
  if (status === 'cancelled' || status === 'refunded') return 'cancelled';
  if (ctx.delivered) return 'success';
  if (!PAYABLE.has(status)) return 'waiting';

  if (isGatewayOrder(order)) {
    const attempt = order.gatewayPayment?.status;
    if (attempt && DEAD_ATTEMPT.has(attempt)) return 'gateway_failed';
    if (attempt && LIVE_ATTEMPT.has(attempt)) return 'waiting';
    return ctx.returned ? 'waiting' : 'gateway_unpaid';
  }

  const manual =
    order.paymentMethodSettlement === 'manual' ||
    (order.paymentMethodSettlement == null && Boolean(order.paymentMethodCode));
  return manual && status === 'pending' ? 'manual_pending' : 'waiting';
}

/**
 * May a member who is waiting pay again? Only for an online order whose attempt
 * never reported money moving. An instant reject can send no webhook at all,
 * and without this the screen would wait forever. Never over `pending` (that
 * payment can still arrive) or `success`.
 */
export function canPayAgain(order: OutcomeOrder): boolean {
  if (!PAYABLE.has(order.status) || !isGatewayOrder(order)) return false;
  const attempt = order.gatewayPayment?.status;
  return !attempt || !LIVE_ATTEMPT.has(attempt);
}

/**
 * How often to re-read the order, or false to stop. A failed attempt keeps a
 * slow poll so a late success still lands on the screen.
 */
export function orderPollMs(order: OutcomeOrder | undefined): number | false {
  if (!order || !POLLED.has(order.status)) return false;
  const attempt = order.gatewayPayment?.status;
  if (isGatewayOrder(order) && attempt && DEAD_ATTEMPT.has(attempt)) return 15_000;
  return 3_000;
}
